import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Camera, Video, Film, Users, Clock, CheckCircle, Image, Star, Car, Percent } from "lucide-react";

const LOGO_FLIX = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/rjk9hmx3_IMG_6380.png";

const MediaPage = () => {
  const mainPackages = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Full Team Action Package",
      subtitle: "Team Photos + Highlight Video",
      price: "$200",
      priceNote: "Starting at",
      features: [
        "Game action coverage",
        "Team shots",
        "1-2 minute team highlight video",
        "10-20 Edited action shots"
      ]
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Player Spotlight Package",
      subtitle: "Player Photos + Highlight Video",
      price: "$150",
      priceNote: "Starting at per player",
      features: [
        "Individual in-game coverage",
        "10-20 edited action shots",
        "45-60 second highlight reel",
        "Digital delivery"
      ]
    }
  ];

  const photographyPackages = [
    {
      title: "Full Team Action Coverage",
      subtitle: "Photography Only",
      price: "$100",
      priceNote: "Starting at",
      features: [
        "Full game coverage",
        "20-50 edited images",
        "Online gallery"
      ]
    },
    {
      title: "Player Action Coverage",
      subtitle: "Individual Photography Only",
      price: "$50",
      priceNote: "Starting at per player",
      features: [
        "10-15 edited action shots",
        "Digital gallery"
      ]
    }
  ];

  const travelFees = [
    { distance: "Events 20+ miles from home base", fee: "$20 travel fee" },
    { distance: "40+ miles", fee: "Custom quote" }
  ];

  const teamDiscounts = [
    { players: "5+ players booking together", discount: "$10 off per player" },
    { players: "10+ players", discount: "$15 off per player" },
    { players: "Multi-game bookings", discount: "Discounted custom package" }
  ];

  const useCases = [
    "College recruiting profiles",
    "Professional highlight reels",
    "Social media content",
    "Team marketing materials",
    "Personal athlete branding",
    "NIL opportunities"
  ];

  return (
    <div className="min-h-screen" data-testid="media-page">
      {/* Hero */}
      <section className="hero-bg py-24 relative">
        <div className="absolute inset-0 athletic-pattern"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="max-w-3xl">
              <p className="text-[#E10600] font-bold uppercase tracking-widest mb-4">Sports Media Specialists</p>
              <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-white mb-4">
                MEDIA<br /><span className="text-[#E10600]">SERVICES</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-400 mb-2">Affordable. High-Quality. Game Ready.</p>
              <p className="text-gray-500 mb-8 text-sm md:text-base">Get seen. Professional photography and videography that showcases your talent.</p>
              <Link to="/booking?category=media" data-testid="media-hero-book">
                <Button className="cta-btn-primary text-base md:text-lg">
                  Book Media Session
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="hidden md:block">
              <img src={LOGO_FLIX} alt="Top Notch Flix" className="h-48 lg:h-64 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Packages */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="main-packages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">SPORTS MEDIA PRICING</h2>
          <p className="text-gray-400 text-center mb-12">Complete packages for teams and individuals</p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {mainPackages.map((pkg, index) => (
              <Card key={index} className="bg-[#111111] border-[#2A2A2A] hover:border-[#E10600] transition-all" data-testid={`main-package-${index}`}>
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#E10600] flex items-center justify-center flex-shrink-0">
                      {pkg.icon}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl text-white">{pkg.title}</h3>
                      <p className="text-gray-400">{pkg.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-gray-500 text-sm">{pkg.priceNote}</p>
                    <span className="text-[#E10600] font-bold text-4xl">{pkg.price}</span>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-4">Includes:</p>
                  <ul className="space-y-2 mb-6">
                    {pkg.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#E10600] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/booking?category=media">
                    <Button className="w-full cta-btn-primary">Book Now</Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photography Only Packages */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl text-white text-center mb-12">PHOTOGRAPHY ONLY</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {photographyPackages.map((pkg, index) => (
              <Card key={index} className="bg-[#1a1a1a] border-[#2A2A2A] hover:border-[#E10600] transition-all" data-testid={`photo-package-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Camera className="w-6 h-6 text-[#E10600]" />
                    <div>
                      <h3 className="font-display text-xl text-white">{pkg.title}</h3>
                      <p className="text-gray-400 text-sm">{pkg.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs">{pkg.priceNote}</p>
                    <span className="text-[#E10600] font-bold text-3xl">{pkg.price}</span>
                  </div>
                  
                  <ul className="space-y-2">
                    {pkg.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#E10600] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Fees & Discounts */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Travel Fee */}
            <Card className="bg-[#111111] border-[#2A2A2A]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Car className="w-6 h-6 text-[#E10600]" />
                  <CardTitle className="font-display text-xl text-white">Travel Fee</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {travelFees.map((fee, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-[#2A2A2A] last:border-0">
                      <span className="text-gray-400 text-sm">{fee.distance}</span>
                      <span className="text-white font-medium">{fee.fee}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Team Discounts */}
            <Card className="bg-[#111111] border-[#2A2A2A]">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Percent className="w-6 h-6 text-[#E10600]" />
                  <CardTitle className="font-display text-xl text-white">Team Discounts</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {teamDiscounts.map((discount, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-[#2A2A2A] last:border-0">
                      <span className="text-gray-400 text-sm">{discount.players}</span>
                      <span className="text-[#E10600] font-medium">{discount.discount}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Deposit Notice */}
      <section className="py-8 bg-[#E10600]/10 border-y border-[#E10600]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white font-medium">
            <span className="text-[#E10600] font-bold">20% deposit required</span> upon booking for all services
          </p>
        </div>
      </section>

      {/* What We Capture */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
                CONTENT THAT <span className="text-[#E10600]">CONVERTS</span>
              </h2>
              <p className="text-gray-400 mb-8">
                In today's recruiting landscape, visual content is everything. We create media that helps you stand out in a sea of athletes competing for attention.
              </p>
              <ul className="space-y-4">
                {useCases.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E10600] flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1a1a1a] rounded-lg border border-[#2A2A2A] p-8 text-center aspect-video flex items-center justify-center">
              <div>
                <Video className="w-16 h-16 text-[#E10600] mx-auto mb-4" />
                <p className="font-display text-2xl text-white mb-2">SAMPLE REEL</p>
                <p className="text-gray-500 text-sm">Highlight reel preview coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Reel */}
      <section className="py-16 bg-[#111111]" data-testid="sample-reel">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-4">
            SAMPLE <span className="text-[#E10600]">REEL</span>
          </h2>
          <p className="text-gray-400 text-center mb-8">See our work in action</p>
          <div className="relative rounded-xl overflow-hidden border-2 border-[#2A2A2A] shadow-2xl bg-black">
            <div className="aspect-[16/9]">
              <video 
                controls 
                className="w-full h-full object-contain bg-black"
                poster="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/dtnd5mo1_FullSizeRender.jpeg"
              >
                <source src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/atclv3pc_4771D5F2-F9E1-4A8E-89EA-68335F22B41F.mov" type="video/quicktime" />
                <source src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/atclv3pc_4771D5F2-F9E1-4A8E-89EA-68335F22B41F.mov" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl text-white text-center mb-12">
            OUR <span className="text-[#E10600]">WORK</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="group aspect-[3/4] rounded-xl overflow-hidden border-2 border-[#2A2A2A] hover:border-[#E10600] transition-all duration-300 shadow-lg" data-testid="portfolio-item-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/dtnd5mo1_FullSizeRender.jpeg" 
                alt="Top Notch coaches with athlete" 
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="group aspect-[3/4] rounded-xl overflow-hidden border-2 border-[#2A2A2A] hover:border-[#E10600] transition-all duration-300 shadow-lg" data-testid="portfolio-item-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/ptw5hayv_IMG_4380.jpeg" 
                alt="Young athlete ready for action" 
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="group aspect-[3/4] rounded-xl overflow-hidden border-2 border-[#2A2A2A] hover:border-[#E10600] transition-all duration-300 shadow-lg" data-testid="portfolio-item-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/dsvy1m28_IMG_4874.jpeg" 
                alt="Top Notch athletes" 
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="group aspect-[3/4] rounded-xl overflow-hidden border-2 border-[#2A2A2A] hover:border-[#E10600] transition-all duration-300 shadow-lg" data-testid="portfolio-item-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/7a3t0ko4_IMG_2041.jpeg" 
                alt="Training session" 
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
          <div className="text-center mt-10">
          <Link to="/booking?category=media" data-testid="view-gallery-link">
            <Button variant="outline" className="border-[#E10600] text-[#E10600] hover:bg-[#E10600] hover:text-white">
              Book Your Session
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#E10600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
            GET SEEN. GET RECRUITED.
          </h2>
          <p className="text-white/80 mb-8">
            Your talent deserves to be showcased. Let's create content that opens doors.
          </p>
          <Link to="/booking?category=media" data-testid="media-cta-book">
            <Button className="bg-white text-[#0A0A0A] hover:bg-gray-100 font-bold text-lg px-8 py-6">
              BOOK MEDIA SESSION
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
